import { useTranslation } from "react-i18next";
import { useSession } from "../../session/SessionContext";
import type { Theme } from "../../theme/useTheme";
import styles from "./Footer.module.css";
import { Tooltip } from "../shared/Tooltip";

const cookieNoticeUrl = "https://www.rws.com/legal/privacy/cookie-notice/";
const feedbackUrl = "https://github.com/rws-product-technology/aia-portal/issues";
const iconProps = {
  className: styles.icon,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

/**
 * A compact, fixed-height footer with the copyright and a cookie information icon link. When the header
 * runs out of room (`iconsInFooter`) it also hosts the theme toggle and, for signed-in users, the
 * feedback and help links, which the header shows itself while there is room.
 */
export function Footer({
  theme,
  onToggleTheme,
  iconsInFooter,
}: {
  theme: Theme;
  onToggleTheme: () => void;
  iconsInFooter: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const isSignedIn = snapshot.status === "signed-in" || snapshot.status === "unclassified";
  const year = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      <span className={styles.copyright}>{t("footer.copyright", { year })}</span>
      <nav aria-label={t("footer.navigationLabel")} className={styles.links}>
        {iconsInFooter && (
          <>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={theme === "dark" ? t("theme.switchToLight") : t("theme.switchToDark")}
              onClick={onToggleTheme}
            >
              {theme === "dark" ? (
                <svg {...iconProps}>
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2m0 16v2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42M2 12h2m16 0h2M4.93 19.07l1.42-1.42m11.3-11.3 1.42-1.42" />
                </svg>
              ) : (
                <svg {...iconProps}>
                  <path d="M20.9 13A9 9 0 0 1 11 3.1 9 9 0 1 0 20.9 13Z" />
                </svg>
              )}
            </button>
            {isSignedIn && (
              <Tooltip label={t("feedback.giveFeedback")} align="end">
                <a
                  href={feedbackUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("feedback.giveFeedback")}
                >
                  <svg {...iconProps}>
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                  </svg>
                </a>
              </Tooltip>
            )}
            {isSignedIn && (
              <Tooltip label={t("help.openHelp")} align="end">
                <a href="/help" aria-label={t("help.openHelp")}>
                  <svg {...iconProps}>
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 2-3 4" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </a>
              </Tooltip>
            )}
          </>
        )}
        <Tooltip label={t("footer.cookies")} align="end">
          <a href={cookieNoticeUrl} aria-label={t("footer.cookies")}>
            <svg {...iconProps}>
              <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5z" />
              <path d="M8.5 8.5v.01" />
              <path d="M16 15.5v.01" />
              <path d="M12 12v.01" />
              <path d="M11 17v.01" />
              <path d="M7 14v.01" />
            </svg>
          </a>
        </Tooltip>
      </nav>
    </footer>
  );
}
